import { useCallback, useEffect, useRef } from 'react';
import { EventSourceParserStream } from 'eventsource-parser/stream';
import * as Sentry from '@sentry/react';

const API_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// A turn acted on immediately after enqueueing can 404 until the background worker has created
// the turn row, so subscribe and cancel both retry for a short window before giving up.
const TURN_NOT_FOUND_RETRY_LIMIT = 10;
const TURN_NOT_FOUND_RETRY_DELAY_MS = 500;

type StreamChunkHandler = (rawData: string) => void;

// Whether the run ended because a newer stream superseded it (or it was cancelled), rather than
// finishing on its own. An aborted run must not run its trailing state cleanup: the chat hook is a
// single shared instance, so the run that aborted it now owns the shared state. `interrupted` marks
// a run that stopped without the terminator and could not be resumed, so the caller keeps the
// partial answer but does not present it as complete.
export type StreamOutcome = { aborted: boolean; interrupted?: boolean };

// How many times a dropped turn subscription reconnects before giving up, and the backoff between
// attempts. Backoff grows exponentially from the base up to the cap, so a brief blip recovers fast
// while a longer outage does not hammer the endpoint.
const TURN_STREAM_MAX_RECONNECT_ATTEMPTS = 5;
const TURN_STREAM_RECONNECT_BASE_MS = 500;
const TURN_STREAM_RECONNECT_MAX_MS = 8000;

const delay = (ms: number) => new Promise(resolve => setTimeout(resolve, ms));

const reconnectBackoffMs = (attempt: number): number =>
  Math.min(TURN_STREAM_RECONNECT_BASE_MS * 2 ** (attempt - 1), TURN_STREAM_RECONNECT_MAX_MS);

const withAfterSeq = (url: string, afterSeq: number | null): string =>
  afterSeq === null ? url : `${url}${url.includes('?') ? '&' : '?'}afterSeq=${afterSeq}`;

// Whether the stream reached the `\n\n` terminator (a genuine end) rather than stopping early. The
// reader signalling `done` before the terminator is a dropped connection, not a finished turn, and
// is the signal to reconnect or mark the answer interrupted. Each event's `seq` arrives on the SSE
// `id` line, so `onSeq` reports the cursor to resume from.
type PipeResult = { sawTerminator: boolean };

async function pipeEventStream(
  response: Response,
  onChunk: StreamChunkHandler,
  onSeq?: (seq: number) => void,
): Promise<PipeResult> {
  if (response.body === null) return { sawTerminator: false };

  const reader = response.body
    .pipeThrough(new TextDecoderStream())
    .pipeThrough(new EventSourceParserStream())
    .getReader();

  while (true) {
    const { value, done } = await reader.read();
    if (done) return { sawTerminator: false };
    if (value.data === '\n\n') return { sawTerminator: true };
    if (value.id) {
      const seq = Number(value.id);
      if (Number.isInteger(seq)) onSeq?.(seq);
    }
    if (value.data) {
      onChunk(value.data);
    }
  }
}

export function useChatTransport() {
  const abortControllerRef = useRef<AbortController | null>(null);

  useEffect(() => {
    return () => {
      abortControllerRef.current?.abort();
    };
  }, []);

  const streamRequest = useCallback(
    async (
      url: string,
      body: object,
      onChunk: StreamChunkHandler,
      onError?: (error: unknown) => void,
    ): Promise<StreamOutcome> => {
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      try {
        const response = await fetch(url, {
          method: 'POST',
          body: JSON.stringify(body),
          headers: { 'Content-Type': 'application/json' },
          credentials: 'include',
          signal: abortController.signal,
        });

        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`Failed to fetch stream: ${response.status} ${errorText}`);
        }

        const { sawTerminator } = await pipeEventStream(response, onChunk);
        // The live path has no persisted turn to resume, so a premature close cannot reconnect;
        // report it as interrupted so the partial answer is not shown as a complete reply.
        return { aborted: false, interrupted: !sawTerminator };
      } catch (error) {
        if (abortController.signal.aborted) return { aborted: true };
        Sentry.captureException(error);
        onError?.(error);
        return { aborted: false };
      }
    },
    [],
  );

  const subscribeToTurn = useCallback(
    async (
      url: string,
      onChunk: StreamChunkHandler,
      onError?: (error: unknown) => void,
      onReconnecting?: (reconnecting: boolean) => void,
    ): Promise<StreamOutcome> => {
      abortControllerRef.current?.abort();
      const abortController = new AbortController();
      abortControllerRef.current = abortController;

      let lastSeq: number | null = null;
      const trackSeq = (seq: number) => {
        lastSeq = seq;
      };

      try {
        let response: Response | null = null;
        for (let attempt = 0; attempt <= TURN_NOT_FOUND_RETRY_LIMIT; attempt += 1) {
          response = await fetch(url, {
            method: 'GET',
            credentials: 'include',
            signal: abortController.signal,
          });
          if (response.status !== 404 || attempt === TURN_NOT_FOUND_RETRY_LIMIT) break;
          await delay(TURN_NOT_FOUND_RETRY_DELAY_MS);
        }

        if (response === null || !response.ok) {
          throw new Error(`Failed to subscribe to turn stream: ${response?.status}`);
        }

        // Consume the stream; on a premature close reconnect from the last seq seen so the answer
        // continues rather than appearing finished. Reconnects stay silent until one fails to come
        // back immediately, and are bounded so a turn whose stream never resumes is marked
        // interrupted instead of retrying forever.
        let reconnectAttempt = 0;
        while (true) {
          const seqBefore: number | null = lastSeq;
          let sawTerminator = false;
          try {
            ({ sawTerminator } = await pipeEventStream(response, onChunk, trackSeq));
          } catch (error) {
            if (abortController.signal.aborted) return { aborted: true };
            Sentry.captureException(error);
          }

          if (sawTerminator) {
            onReconnecting?.(false);
            return { aborted: false };
          }
          if (abortController.signal.aborted) return { aborted: true };

          // The last connection delivered new events, so reset the budget: only a run of attempts
          // with no progress at all should exhaust it.
          if (lastSeq !== seqBefore) reconnectAttempt = 0;

          let reconnected = false;
          while (!reconnected) {
            reconnectAttempt += 1;
            if (reconnectAttempt > TURN_STREAM_MAX_RECONNECT_ATTEMPTS) {
              onReconnecting?.(false);
              return { aborted: false, interrupted: true };
            }
            // Keep the first retry silent so a momentary blip does not flash a reconnecting state.
            if (reconnectAttempt >= 2) onReconnecting?.(true);
            await delay(reconnectBackoffMs(reconnectAttempt));
            if (abortController.signal.aborted) return { aborted: true };
            try {
              const next = await fetch(withAfterSeq(url, lastSeq), {
                method: 'GET',
                credentials: 'include',
                signal: abortController.signal,
              });
              if (next.ok) {
                response = next;
                reconnected = true;
              }
            } catch (error) {
              if (abortController.signal.aborted) return { aborted: true };
              Sentry.captureException(error);
            }
          }
          onReconnecting?.(false);
        }
      } catch (error) {
        if (abortController.signal.aborted) return { aborted: true };
        Sentry.captureException(error);
        onError?.(error);
        return { aborted: false };
      }
    },
    [],
  );

  const abort = useCallback(() => {
    abortControllerRef.current?.abort();
    abortControllerRef.current = null;
  }, []);

  const buildTurnCancelUrl = useCallback(
    (conversationId: string, turnId: string) =>
      `${API_URL}/conversations/${conversationId}/turns/${turnId}/cancel`,
    [],
  );

  // Tell the backend to stop the turn's background generation. Uses its own request rather than
  // the shared abort controller, so cancelling does not depend on the subscription still being
  // open, and retries the worker-start 404 window like the subscribe path.
  const cancelTurn = useCallback(
    async (conversationId: string, turnId: string): Promise<void> => {
      for (let attempt = 0; attempt <= TURN_NOT_FOUND_RETRY_LIMIT; attempt += 1) {
        try {
          const response = await fetch(buildTurnCancelUrl(conversationId, turnId), {
            method: 'POST',
            credentials: 'include',
          });
          if (response.status !== 404 || attempt === TURN_NOT_FOUND_RETRY_LIMIT) return;
        } catch (error) {
          Sentry.captureException(error);
          return;
        }
        await new Promise(resolve => setTimeout(resolve, TURN_NOT_FOUND_RETRY_DELAY_MS));
      }
    },
    [buildTurnCancelUrl],
  );

  const buildClientChatUrl = useCallback(
    (clientId: string) => `${API_URL}/clients/${clientId}/chat`,
    [],
  );

  const buildCrossClientChatUrl = useCallback(() => `${API_URL}/chat/general`, []);

  const buildFamilyChatUrl = useCallback(
    (familyId: string) => `${API_URL}/families/${familyId}/chat`,
    [],
  );

  const buildDocumentEditUrl = useCallback(
    (entityId: string) => `${API_URL}/documents/${entityId}/multi-turn-edit`,
    [],
  );

  const buildTurnStreamUrl = useCallback(
    (conversationId: string, turnId: string) =>
      `${API_URL}/conversations/${conversationId}/turns/${turnId}/stream`,
    [],
  );

  return {
    streamRequest,
    subscribeToTurn,
    cancelTurn,
    abort,
    buildClientChatUrl,
    buildCrossClientChatUrl,
    buildFamilyChatUrl,
    buildDocumentEditUrl,
    buildTurnStreamUrl,
  };
}
